import React, { useEffect, useMemo } from 'react';
import { partnerApiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useCabinet } from '../../contexts/CabinetContext';
import { getCompleteness } from '../../utils/completeness';
import { VenueCover } from '../home/VenueCover';
import { KpiRow } from '../home/KpiRow';
import { TrafficPanel } from '../home/TrafficPanel';
import { VenueAbout } from '../home/VenueAbout';
import { VenueGallery } from '../home/VenueGallery';
import { CompletenessPanel } from '../home/CompletenessPanel';
import { WorkingHoursPanel } from '../home/WorkingHoursPanel';
import { ContactsPanel } from '../home/ContactsPanel';
import { LegalPanel } from '../home/LegalPanel';
import { VenueMap } from '../home/VenueMap';
import { ReviewsPanel } from '../home/ReviewsPanel';
import { ReviewReportsPanel } from '../home/ReviewReportsPanel';

interface HomePageProps {
  onEdit: () => void;
  /** Форма сразу на разделе «Расположение»: адрес и точка на карте. */
  onEditLocation: () => void;
  /** Форма сразу на разделе «Реквизиты». */
  onEditLegal: () => void;
  onLogout: () => void;
}

/**
 * Главная кабинета: карточка заведения так, как её увидит клиент,
 * плюс то, чего в ней не хватает.
 *
 * Раскладка на две колонки — слева содержимое карточки, справа её
 * состояние. Логика в том, что левая половина отвечает на «что увидит
 * клиент», правая — на «что мне сделать»; смешивать их в один поток
 * значит прятать задачи между блоками контента.
 */
export const HomePage: React.FC<HomePageProps> = ({ onEdit, onEditLocation, onEditLegal }) => {
  const { partner } = useAuth();
  const { stats, reviews, setReviews, isDashboardLoading } = useCabinet();

  // Дашборд не всегда отдаёт отзывы — тогда берём их из публичного эндпоинта.
  useEffect(() => {
    const partnerId = partner?.id;
    if (!partnerId || reviews.length > 0) return;
    let cancelled = false;
    partnerApiService
      .getPartnerReviews(partnerId)
      .then((data) => {
        if (!cancelled) setReviews(data);
      })
      .catch((error) => console.error('Ошибка загрузки отзывов:', error));
    return () => {
      cancelled = true;
    };
  }, [partner?.id, reviews.length, setReviews]);

  const completeness = useMemo(() => getCompleteness(partner ?? null), [partner]);

  return (
    <>
      <VenueCover partner={partner ?? null} onEdit={onEdit} />
      <KpiRow stats={stats} isLoading={isDashboardLoading} />
      <TrafficPanel />

      <div className="qp-grid qp-grid--split">
        <div className="qp-stack">
          <VenueAbout partner={partner ?? null} stats={stats} onEdit={onEdit} />
          <VenueGallery images={partner?.additional_images ?? []} onEdit={onEdit} />
          <ReviewsPanel reviews={reviews} onChange={setReviews} />
          {/* Жалобы — сразу под отзывами: это вторая половина того же
              разговора. Данные панель грузит сама: жалобы есть у единиц,
              тащить их в общий дашборд ради остальных незачем. */}
          <ReviewReportsPanel />
        </div>

        <div className="qp-stack">
          <CompletenessPanel completeness={completeness} onEdit={onEdit} />
          <WorkingHoursPanel hours={partner?.working_hours ?? []} onEdit={onEdit} />
          <ContactsPanel partner={partner ?? null} onEdit={onEdit} />
          <VenueMap partner={partner ?? null} onEdit={onEditLocation} />
          {/* Реквизиты — последними: они необязательные, нужны к договору и
              на карточку в приложении не влияют. */}
          <LegalPanel legal={partner?.legal} onEdit={onEditLegal} />
        </div>
      </div>
    </>
  );
};
